import { useEffect, useState } from "react";
import { api } from "../lib/config";
import { Loader2, Star, DollarSign, UserCheck, MailOpen } from "lucide-react";

/**
 * Admin · Retention & Win-Back stats card.
 * Renders 8 metrics in a responsive grid:
 *   Reviews requested · Reviews completed · Review conversion rate
 *   Tips received · Tip revenue
 *   Reactivated customers · Win-back conversion rate
 *   Abandoned signup recovery rate
 */
export default function RetentionStatsCard() {
  const [loading, setLoading] = useState(true);
  const [d, setD] = useState({});
  useEffect(() => {
    let cancel = false;
    api.get("/admin/retention-stats")
      .then(({ data }) => { if (!cancel) setD(data || {}); })
      .catch(() => { if (!cancel) setD({}); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);
  return (
    <div className="bg-white rounded-3xl border border-gray-200 p-5 sm:p-6 shadow-sm" data-testid="retention-stats-card">
      <div className="flex items-center gap-2">
        <UserCheck className="w-4 h-4 text-[#047857]" />
        <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-700">Retention & Win-Back</div>
      </div>
      <h3 className="mt-1 text-xl font-extrabold tracking-tight font-[Outfit] text-gray-900">How customers come back</h3>
      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading stats…
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <Tile icon={Star} label="Reviews requested" value={d.reviews_requested || 0} testid="rs-reviews-req" />
          <Tile icon={Star} label="Reviews completed" value={d.reviews_completed || 0} testid="rs-reviews-done" />
          <Tile label="Review rate" value={`${d.review_rate || 0}%`} accent testid="rs-review-rate" />
          <Tile icon={DollarSign} label="Tips received" value={d.tips_received || 0} testid="rs-tips" />
          <Tile icon={DollarSign} label="Tip revenue" value={`$${(d.tip_revenue || 0).toFixed(2)}`} accent testid="rs-tip-rev" />
          <Tile icon={UserCheck} label="Reactivated" value={d.reactivated || 0} testid="rs-reactivated" />
          <Tile label="Win-back rate" value={`${d.winback_rate || 0}%`} accent testid="rs-winback-rate" />
          <Tile icon={MailOpen} label="Signup recovery" value={`${d.abandon_rate || 0}%`} accent testid="rs-abandon-rate" />
        </div>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className={`rounded-2xl px-3 py-2.5 border ${accent ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"}`} data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`mt-0.5 text-lg sm:text-xl font-extrabold tabular-nums tracking-tight ${accent ? "text-[#047857]" : "text-gray-900"}`}>{value}</div>
    </div>
  );
}
